<template>
    <div class="log-container">
        <div v-for="(log, index) in sortedLogs" :key="index" class="card mb-2">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
                <span class="badge fs-6"
                    :class="log.type === 'medicine' ? 'bg-success' : 'bg-secondary'"
                >{{ log.timeLabel }}</span>
                <span class="badge"
                    :class="log.type === 'medicine' ? 'bg-success' : 'bg-secondary'"
                >
                    <i class="align-middle fas fa-fw "
                        :class="log.type === 'medicine' ? 'fa-heart-pulse' : 'fa-syringe'"
                    ></i>
                    <span>{{ log.type === 'medicine' ? '麻醉用藥' : '生命體徵' }}</span>
                </span>
            </div>
            <div class="card-body py-2">
                <!-- Vital Sign Block -->
                <div v-if="log.type === 'vital-sign'" class="event-log-grid">
                    <div class="log-item">
                        <label>Iso</label>
                        <span>
                            {{ log.data.Iso ?? 'N/A' }}
                            <i v-if="log.trends?.Iso === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.Iso === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>SpO2</label>
                        <span>
                            {{ log.data.SpO2 }}%
                            <i v-if="log.trends?.SpO2 === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.SpO2 === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>EtCO2</label>
                        <span>
                            {{ log.data.EtCO2 }}
                            <i v-if="log.trends?.EtCO2 === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.EtCO2 === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>HR</label>
                        <span>
                            {{ log.data.HR }}
                            <i v-if="log.trends?.HR === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.HR === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>RR</label>
                        <span>
                            {{ log.data.RR }}
                            <i v-if="log.trends?.RR === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.RR === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>BT</label>
                        <span>
                            {{ log.data.BT }}
                            <i v-if="log.trends?.BT === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.BT === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>BP (S/D/M)</label>
                        <span>
                            {{ log.data.SYS }}/{{ log.data.DIA }}/{{ log.data.MAP }}
                            <i v-if="log.trends?.MAP === 'up'" class="fas fa-arrow-up text-danger ms-1 small-icon"></i>
                            <i v-else-if="log.trends?.MAP === 'down'" class="fas fa-arrow-down text-success ms-1 small-icon"></i>
                        </span>
                    </div>
                     <div class="log-item">
                        <label>Fluid</label>
                        <span>{{ log.data.Fluid_rate }}</span>
                    </div>
                     <div class="log-item full-width" v-if="log.data.Event">
                        <label>Event</label>
                        <span>{{ log.data.Event }}</span>
                    </div>
                </div>

                <!-- Medicine Block -->
                <div v-else-if="log.type === 'medicine'" class="event-log-grid">
                     <div class="log-item">
                        <label>Drug</label>
                        <span>{{ log.data.item_name }}</span>
                    </div>
                     <div class="log-item">
                        <label>Dosage (actual)</label>
                        <span>{{ log.data.dosage_actual }}</span>
                    </div>
                     <div class="log-item">
                        <label>Volume (actual)</label>
                        <span>{{ log.data.volume_actual }}</span>
                    </div>
                     <div class="log-item">
                        <label>Route</label>
                        <span>{{ log.data.router_name }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="sortedLogs.length === 0" class="text-center py-4 text-muted">
            尚無記錄
        </div>
    </div>
</template>

<script>
export default {
    name: 'SurgeryLogTable',
    props: {
        logs: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        sortedLogs() {
            // Sort ascending to calculate trends
            const ascLogs = [...this.logs].sort((a, b) => a.timeSeconds - b.timeSeconds);
            
            let lastVital = null;
            
            const processed = ascLogs.map(log => {
                if (log.type !== 'vital-sign') return { ...log };
                
                const trends = {};
                // Fields to check
                const fields = ['Iso', 'SpO2', 'EtCO2', 'HR', 'RR', 'BT', 'SYS', 'DIA', 'MAP', 'Fluid_rate'];
                
                fields.forEach(field => {
                    const currentVal = parseFloat(log.data[field]);
                    
                    if (lastVital && !isNaN(currentVal)) {
                         const lastVal = parseFloat(lastVital.data[field]);
                         if (!isNaN(lastVal)) {
                             if (currentVal > lastVal) trends[field] = 'up';
                             else if (currentVal < lastVal) trends[field] = 'down';
                         }
                    }
                });
                
                lastVital = log; // Update last vital sign log
                
                return {
                    ...log,
                    trends
                };
            });
            
            // Return descending
            return processed.reverse();
        }
    }
}
</script>

<style scoped>
.log-container {
    max-height: 60vh;
    overflow-y: auto;
}
.event-log-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}
.log-item {
    display: flex;
    flex-direction: column;
    min-width: 80px;
    background-color: #f8f9fa;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #e9ecef;
}
.log-item label {
    font-size: 0.75rem;
    color: #6c757d;
    margin-bottom: 2px;
}
.log-item span {
    font-weight: 500;
}
.full-width {
    width: 100%;
}
.small-icon {
    font-size: 0.8rem;
    vertical-align: middle;
}
</style>
